import {
  TIPOLOGIE_IMPIANTO,
  type CodiceContatore,
  type Periodo,
  type TipologiaImpianto,
} from '@praticheenergia/domain'
import { dec, type Arrotondamento, type Dec } from './dec'
import type { Formula } from './formula'
import { formulaDaJson } from './formula-json'
import type { QuadroModello } from './modello'
import { verificaTracciato, type Tracciato } from './tracciato'

/** Un importo fisso vale una volta per mese e ambito, a prescindere dai kWh. */
export type Aliquota = Dec | { readonly fissoMensile: Dec }

export interface Scaglioni {
  readonly soglia: Dec
  readonly sogliaAlta: Dec
  readonly moltiplicaPerUtenze: boolean
  /** Tipologie delle righe da ripartire: in energix `M4+M5+M6+M7`. */
  readonly tipologieSoggette: readonly string[]
  readonly righi: {
    readonly entroAlta: readonly [string, string]
    readonly oltreAlta: readonly [string, string]
  }
}

export type ControlloPeriodo =
  | { readonly codice: string; readonly tipo: 'codice_ditta_pattern'; readonly pattern: string }
  | { readonly codice: string; readonly tipo: 'contatore_richiede_tipologia'; readonly contatore: CodiceContatore }
  | { readonly codice: string; readonly tipo: 'contatore_richiede_scambio'; readonly contatore: CodiceContatore }
  | { readonly codice: string; readonly tipo: 'debito_inizio_vietato' }

/** Una riga di `regola_fiscale`: un nuovo anno o periodo e' un nuovo seme, non un rilascio. */
export interface RegolePeriodo {
  readonly annoImposta: number
  readonly periodo: Periodo
  readonly versione: string
  /** `periodo`: un solo consumo per contatore (annuale 2026). */
  readonly granularita: 'mese' | 'periodo'
  readonly arrotondamento: Arrotondamento
  readonly ambiti: { readonly predefinito: number; readonly perProvincia: Readonly<Record<string, number>> }
  readonly aliquote: Readonly<Record<string, Aliquota>>
  readonly scaglioni?: Scaglioni | undefined
  /** Consumo del quadro M per le tipologie con `quadroM` configurato. */
  readonly formulaQuadroM?: Formula | undefined
  /** Le tipologie che dichiarano con questo periodo: apri_dichiarazioni
   * (0032_apertura_dichiarazione.sql) apre un periodo solo per queste. */
  readonly tipologie: readonly TipologiaImpianto[]
  readonly quadriEsclusi: Readonly<Partial<Record<TipologiaImpianto, readonly QuadroModello[]>>>
  readonly controlli: readonly ControlloPeriodo[]
  readonly tracciato: Tracciato
}

export class RegolePeriodoIncomplete extends Error {}

type Grezzo = Record<string, unknown>

export function regolePeriodoDaJson(raw: Grezzo): RegolePeriodo {
  const aliquote = Object.fromEntries(
    Object.entries(raw.aliquote as Record<string, string | { fissoMensile: string }>).map(([rigo, v]) => [
      rigo,
      typeof v === 'string' ? dec(v) : { fissoMensile: dec(v.fissoMensile) },
    ]),
  )
  const s = raw.scaglioni as (Omit<Scaglioni, 'soglia' | 'sogliaAlta'> & { soglia: string; sogliaAlta: string }) | undefined

  return {
    ...raw,
    aliquote,
    scaglioni: s === undefined ? undefined : { ...s, soglia: dec(s.soglia), sogliaAlta: dec(s.sogliaAlta) },
    formulaQuadroM: raw.formulaQuadroM === undefined ? undefined : formulaDaJson(raw.formulaQuadroM as Grezzo),
  } as unknown as RegolePeriodo
}

/** Un set incoerente si scopre al caricamento, non alla generazione del file da trasmettere. */
export function caricaRegolePeriodo(r: RegolePeriodo): RegolePeriodo {
  const errori = verificaTracciato(r.tracciato)

  const s = r.scaglioni
  if (s !== undefined) {
    for (const rigo of [...s.righi.entroAlta, ...s.righi.oltreAlta]) {
      if (!(rigo in r.aliquote)) errori.push(`rigo di scaglione senza aliquota: ${rigo}`)
    }
  }

  const tipologie: readonly string[] = TIPOLOGIE_IMPIANTO
  for (const t of r.tipologie) {
    if (!tipologie.includes(t)) errori.push(`tipologia sconosciuta in tipologie: ${t}`)
  }
  for (const t of Object.keys(r.quadriEsclusi)) {
    if (!tipologie.includes(t)) errori.push(`tipologia sconosciuta in quadriEsclusi: ${t}`)
    else if (!(r.tipologie as readonly string[]).includes(t)) {
      errori.push(`quadriEsclusi per una tipologia che non usa il periodo: ${t}`)
    }
  }

  if (errori.length > 0) throw new RegolePeriodoIncomplete(errori.join('; '))
  return r
}
